import { ArcRotateCamera } from '@/infrastructure/engine3d/camera/ArcRotateCamera';
import { CameraController } from '@/infrastructure/engine3d/camera/CameraController';
import { EnvironmentMapLoader } from '@/infrastructure/engine3d/environment/EnvironmentMapLoader';
import { MainLoop } from '@/infrastructure/engine3d/mainLoop/MainLoop';
import { ModelLoader } from '@/infrastructure/engine3d/assets/ModelLoader';
import { WebGLContextRecoveryController } from '@/infrastructure/engine3d/lifecycle/WebGLContextRecoveryController';
import { Scene, Vector3, VSMShadowMap, WebGLRenderer } from 'three';
import type { I3DEngine } from '@/application/ports/I3DEngine';
import type { ICameraController } from '@/application/ports/ICameraController';
import type { IProduct3DStrategy } from '@/infrastructure/engine3d/strategies/IProduct3DStrategy';
import type { IViewportEngine3D } from '@/application/ports/IViewportEngine3D';
import type { TCameraFraming } from '@/application/ports/TCameraFraming';
import type { TCameraParams } from '@/infrastructure/engine3d/camera/TCameraParams';
import type { TCanvasSnapshot } from '@/application/ports/TCanvasSnapshot';
import type { TSide } from '@/domain/shared/TSide';
import type { TStrategyAttachContext } from '@/infrastructure/engine3d/strategies/TStrategyAttachContext';
import type { CustomOrbitControls } from '@/infrastructure/engine3d/camera/controls/CustomOrbitControls';

type TThreeEngine3DParams = {
    canvas: HTMLCanvasElement;
    container: HTMLElement;
    isMobile?: boolean;
};

// Product-agnostic three.js adapter for the I3DEngine port. Owns the
// renderer, scene, camera and render loop; delegates every product
// concern to an IProduct3DStrategy. One instance per Vue mount.
class ThreeEngine3D implements I3DEngine, IViewportEngine3D {
    private readonly _canvas: HTMLCanvasElement;
    private readonly _container: HTMLElement;
    private readonly _contextRecovery: WebGLContextRecoveryController;
    private readonly _isMobile: boolean;
    private readonly _mainLoop: MainLoop;
    private readonly _renderer: WebGLRenderer;
    private readonly _scene: Scene;
    private _afterRenderCallbacks: (() => void)[] = [];
    private _camera: ArcRotateCamera;
    private _cameraController: CameraController;
    private _strategy: IProduct3DStrategy | null = null;

    constructor(params: TThreeEngine3DParams) {
        this._canvas = params.canvas;
        this._container = params.container;
        this._isMobile = params.isMobile ?? false;
        this._scene = new Scene();
        this._renderer = this._setupRenderer();
        this._camera = this._setupCamera();
        this._cameraController = new CameraController({
            camera: this._camera,
            getStrategyRoot: () => this._strategy?.getRoot(),
        });
        this._mainLoop = this._setupMainLoop();
        this._contextRecovery = new WebGLContextRecoveryController({
            canvasElement: this._canvas,
            mainLoop: this._mainLoop,
            onContextRestored: () => this._strategy?.onContextRestored?.(this._buildAttachContext()),
        });

        this._mainLoop.start();
        this.resize();
    }

    // Off-port getter — createThreeAdapters plumbs the CameraController
    // into Preview3DController so Vue writes `preview.camera.zoomIn()`.
    getCameraController(): ICameraController {
        return this._cameraController;
    }

    // Infrastructure-facing wiring (kept off the I3DEngine port).
    // The full-lifecycle overload attaches synchronously + returns the
    // strategy's async attach; the sync overload just registers.
    attachStrategy(strategy: IProduct3DStrategy): Promise<void> {
        if (this._strategy) {
            this.detachStrategy();
        }
        this._strategy = strategy;
        // Single framing application AFTER attach() has resolved — the
        // strategy owns its measured dimensions at this point and
        // computes the final framing from them. The Vue layer hides the
        // canvas until this promise resolves, so no mesh-less flash.
        return strategy.attach(this._buildAttachContext()).then(() => {
            this._setCameraFraming(strategy.getCameraFraming({ aspect: this._camera.aspect, fov: this._camera.fov }));
        });
    }

    // Off-port capability accessor — strategies read it at construction
    // (e.g. TextureBridge in GobeletStrategy).
    getMaxAnisotropy(): number {
        return this._renderer.capabilities.getMaxAnisotropy();
    }

    detachStrategy(): void {
        this._strategy?.detach(this._buildAttachContext());
        this._strategy = null;
        // detach() disposed the cached GLTF's geometries; the entry would resolve to a
        // destroyed mesh.
        ModelLoader.clear();
    }

    dispose(): void {
        this._contextRecovery.dispose();
        this._mainLoop.dispose();
        this.detachStrategy();
        this._afterRenderCallbacks.length = 0;
        this._renderer.dispose();
        if (!this._renderer.getContext().isContextLost()) {
            this._renderer.forceContextLoss();
        }
        EnvironmentMapLoader.clear();
    }

    resize(): void {
        const { clientHeight, clientWidth } = this._container;
        if (clientWidth <= 0 || clientHeight <= 0) {
            return;
        }
        this._renderer.setSize(clientWidth, clientHeight);
        this._camera.aspect = clientWidth / clientHeight;
        this._camera.updateProjectionMatrix();
        this._renderer.render(this._scene, this._camera);
    }

    applyCanvasTexture(side: TSide, snapshot: TCanvasSnapshot): void {
        this._strategy?.applyCanvasTexture(side, snapshot);
    }

    onAfterRender(callback: () => void): () => void {
        this._afterRenderCallbacks.push(callback);
        return () => {
            this._afterRenderCallbacks = this._afterRenderCallbacks.filter((cb) => cb !== callback);
        };
    }

    private _buildAttachContext(): TStrategyAttachContext {
        return { renderer: this._renderer, scene: this._scene };
    }

    private _setCameraFraming(framing: TCameraFraming): void {
        this._camera.position.set(framing.position.x, framing.position.y, framing.position.z);
        const target = new Vector3(framing.target.x, framing.target.y, framing.target.z);
        this._camera.lookAt(target);
        const controls = this._camera.controls as CustomOrbitControls;
        controls.target.copy(target);
        if (framing.minDistance !== undefined) {
            controls.minDistance = framing.minDistance;
        }
        if (framing.maxDistance !== undefined) {
            controls.maxDistance = framing.maxDistance;
        }
        if (framing.minPolarAngle !== undefined) {
            controls.minPolarAngle = framing.minPolarAngle;
        }
        if (framing.maxPolarAngle !== undefined) {
            controls.maxPolarAngle = framing.maxPolarAngle;
        }
        // setPhi/setTheta/setRadius derive position from controls.target
        // — must run AFTER controls.target is set above.
        if (framing.spherical) {
            this._camera.setPhi(framing.spherical.phi);
            this._camera.setTheta(framing.spherical.theta);
            this._camera.setRadius(framing.spherical.radius);
        }
        controls.update();
    }

    private _setupRenderer(): WebGLRenderer {
        const renderer = new WebGLRenderer({ canvas: this._canvas, antialias: true });
        renderer.debug.checkShaderErrors = import.meta.env.DEV;
        renderer.setPixelRatio(this._isMobile ? Math.min(window.devicePixelRatio, 1.2) : Math.min(window.devicePixelRatio, 2));
        renderer.shadowMap.enabled = !this._isMobile;
        if (renderer.shadowMap.enabled) {
            // VSM — softer, required by CardStrategy's directional key light.
            renderer.shadowMap.type = VSMShadowMap;
        }
        renderer.sortObjects = true;
        return renderer;
    }

    private _setupCamera(): ArcRotateCamera {
        const cameraParams: TCameraParams = {
            aspect: this._container.clientWidth / this._container.clientHeight || 1,
            far: this._isMobile ? 50 : 100,
            fov: 40,
            name: 'engine3d-camera',
            near: 0.1,
            rendererDomElement: this._renderer.domElement,
        };
        const camera = new ArcRotateCamera(cameraParams);
        const controls = camera.controls as CustomOrbitControls;
        controls.enableDamping = true;
        controls.dampingFactor = this._isMobile ? 0.25 : 0.15;
        controls.enablePan = false;
        controls.rotateSpeed = this._isMobile ? 1.0 : 1.5;
        return camera;
    }

    private _setupMainLoop(): MainLoop {
        const mainLoop = new MainLoop();
        mainLoop.idleFps = -1;
        mainLoop.addCallback(() => {
            (this._camera.controls as CustomOrbitControls).update();
            this._camera.render();
            this._strategy?.update?.();
            this._renderer.render(this._scene, this._camera);
            this._afterRenderCallbacks.forEach((cb) => cb());
        });
        return mainLoop;
    }
}

export { ThreeEngine3D };
